import RadioButton from "@rio-cloud/rio-uikit/RadioButton";
import type { BatchDeleteScope } from "@types";
import { useT } from "@hooks";

const ICON_CIRCLE_SIZE = 40;

type HeaderTone = "danger" | "success" | "warning";

const TONE_STYLE: Record<HeaderTone, { icon: string; color: string; background: string }> = {
  danger: { icon: "rioglyph-trash", color: "text-color-danger", background: "var(--brand-danger-decent)" },
  success: { icon: "rioglyph-ok", color: "text-color-success", background: "var(--brand-success-decent)" },
  warning: { icon: "rioglyph-warning-sign", color: "text-color-warning", background: "var(--brand-warning-decent)" },
};

interface HeaderProps {
  title: string;
  subtitle?: string;
  tone?: HeaderTone;
  icon?: string;
  aside?: React.ReactNode;
}

export const BatchDeleteHeader = ({ title, subtitle, tone = "danger", icon, aside }: HeaderProps) => {
  const toneStyle = TONE_STYLE[tone];

  return (
    <div className="display-flex align-items-center gap-15 width-100pct">
      <div
        className="display-flex align-items-center justify-content-center rounded-circle"
        style={{
          width: ICON_CIRCLE_SIZE,
          height: ICON_CIRCLE_SIZE,
          flex: "0 0 auto",
          backgroundColor: toneStyle.background,
        }}
      >
        <span className={`rioglyph ${icon ?? toneStyle.icon} ${toneStyle.color} text-size-18`} aria-hidden="true" />
      </div>
      <div className="display-flex flex-column flex-1-1">
        <span className="text-size-18 text-medium text-color-darkest">{title}</span>
        {subtitle && <span className="text-size-12 text-color-dark text-normal">{subtitle}</span>}
      </div>
      {aside}
    </div>
  );
};

export const IrreversibleNotice = () => {
  const t = useT();

  return (
    <div
      className="display-flex gap-10 padding-15 rounded"
      style={{ backgroundColor: "var(--brand-danger-decent)" }}
      data-testid="batch-delete-irreversible"
    >
      <span className="rioglyph rioglyph-warning-sign text-color-danger text-size-16" aria-hidden="true" />
      <div className="display-flex flex-column">
        <span className="text-medium text-color-danger">{t("common.dialog.irreversibleAction")}</span>
        <span className="text-size-12 text-color-danger">{t("batchDelete.notice.audit")}</span>
      </div>
    </div>
  );
};

export const IntegrationNotice = ({ count }: { count: number }) => {
  const t = useT();

  return (
    <div className="bg-lightest rounded padding-15 display-flex gap-10" data-testid="batch-delete-integration">
      <span className="rioglyph rioglyph-lock text-color-dark text-size-16" aria-hidden="true" />
      <div className="display-flex flex-column gap-5">
        <span className="text-size-14 text-medium">{t("batchDelete.confirm.integration.title", { count })}</span>
        <span className="text-size-12 text-color-dark">{t("batchDelete.confirm.integration.hint")}</span>
      </div>
    </div>
  );
};

export interface ScopeOption {
  scope: BatchDeleteScope;
  labelKey: string;
}

interface ScopeOptionsProps {
  name: string;
  options: ScopeOption[];
  value: BatchDeleteScope;
  onChange: (scope: BatchDeleteScope) => void;
}

export const ScopeOptions = ({ name, options, value, onChange }: ScopeOptionsProps) => {
  const t = useT();

  return (
    <div className="display-flex flex-column gap-5" role="radiogroup">
      {options.map((option) => (
        <div
          key={option.scope}
          className={`padding-x-15 padding-y-10 rounded ${value === option.scope ? "bg-highlight-lightest" : ""}`}
        >
          <RadioButton
            name={name}
            value={option.scope}
            checked={value === option.scope}
            onChange={() => onChange(option.scope)}
            label={<span className="text-size-14">{t(option.labelKey)}</span>}
          />
        </div>
      ))}
    </div>
  );
};

interface StepProgressProps {
  total: number;
  current: number;
}

export const StepProgress = ({ total, current }: StepProgressProps) => (
  <div className="display-flex gap-10" data-testid="batch-delete-step-progress">
    {Array.from({ length: total }, (_, index) => (
      <div
        key={`step-${index}`}
        data-testid="batch-delete-step"
        className={`flex-1-1 rounded ${index <= current ? "bg-danger" : "bg-lighter"}`}
        style={{ height: 4 }}
      />
    ))}
  </div>
);
